import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import ThemeToggler from '@components/ThemeToggler.tsx';
import { fileService } from '@services/fileService.ts';
import apiService from '@services/apiService.ts';
import ContextMenu from '@components/ContextMenu/ContextMenu.tsx';
import { ContextMenuItem } from '@components/ContextMenu/ContextMenuItem.tsx';
import { modalService } from './Modal.tsx';
import { CreateShareSpaceDialog } from '@pages/dialogs/CreateShareSpaceDialog.tsx';
import { ModalButtonType } from './Modal.tsx';
import { toastService } from './Toast.tsx';
import { ImgMenu } from './Images/ImgMenu.tsx';
import { ImgLock } from './Images/ImgLock.tsx';
import { ImgSwap } from './Images/ImgSwap.tsx';
import { ImgPower } from './Images/ImgPower.tsx';

export type StorageInfo = { usedSpace: number; storageLimit: number; filesCount?: number; trashUsedSpace?: number };

type PageHeaderProps = {
  breadcrumb: React.ReactNode;
  storageInfo?: StorageInfo | null;
  onLogoClick?: () => void;
  onSidebarToggle?: () => void;
};

export function PageHeader({ breadcrumb, storageInfo, onLogoClick, onSidebarToggle }: PageHeaderProps) {
  const user = window.user!;
  const hasStorage = storageInfo !== undefined;
  const pct = hasStorage && storageInfo && storageInfo.storageLimit > 0
    ? (storageInfo.usedSpace / storageInfo.storageLimit) * 100
    : null;
  const trashPct = hasStorage && storageInfo && storageInfo.storageLimit > 0
    ? (storageInfo.trashUsedSpace || 0) / storageInfo.storageLimit * 100
    : null;
  const totalPct = pct !== null && trashPct !== null ? Math.min(100, pct + trashPct) : null;

  const userProfileRef = useRef<HTMLDivElement>(null);

  const handleLogout = () => {
    window.location.href = '/auth/logout';
  };

  const handleCreateShareSpace = async () => {
    const result = await modalService.show<{}, { name: string; sizeGb: number }>(CreateShareSpaceDialog);
    if (result.button !== ModalButtonType.Add || !result.result) {
      return;
    }

    try {
      const created = await apiService.post<{ link: string }>('/api/file/share-space', {
        name: result.result.name,
        sizeGb: result.result.sizeGb,
      });
      window.location.href = created.link;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to create shared space';
      toastService.error(message, 'Shared Space');
    }
  };

  return (
    <header className="header">
      <div className="header-left">
        {onSidebarToggle && (
          <button className="sidebar-toggle-btn" onClick={onSidebarToggle} title="Toggle sidebar" aria-label="Toggle sidebar">
            <span className="sidebar-toggle-icon"><ImgMenu size={18} /></span>
          </button>
        )}
        {onLogoClick ? (
          <a href="#" className="logo" style={{ textDecoration: 'none', color: 'inherit' }} onClick={(e) => { e.preventDefault(); onLogoClick(); }}><ImgLock /> FileVault</a>
        ) : (
          <Link to="/" className="logo" style={{ textDecoration: 'none', color: 'inherit' }}><ImgLock /> FileVault</Link>
        )}
        <nav className="breadcrumb">
          {breadcrumb}
        </nav>
      </div>
      <div className="header-right">
        <ThemeToggler />
        <div ref={userProfileRef} className="user-profile" onClick={(e) => e.stopPropagation()}>
          <div className="user-avatar">
            {user.picture ? (
              <img src={user.picture} alt={user.email} referrerPolicy="no-referrer" />
            ) : user.emailMd5 ? (
              <img src={`https://www.gravatar.com/avatar/${user.emailMd5}?d=identicon&s=36`} alt={user.email} />
            ) : (
              <div className="user-avatar-placeholder">
                {(user.name || user.given_name || user.email).charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <div className="user-info">
            <div className="user-name">{user.email}</div>
            {hasStorage && (
              <div className="storage-info">
                {storageInfo
                  ? `${fileService.formatFileSize(storageInfo.usedSpace + (storageInfo.trashUsedSpace || 0))} / ${storageInfo.storageLimit ? fileService.formatFileSize(storageInfo.storageLimit) : '∞'}`
                  : '…'}
              </div>
            )}
            {totalPct !== null && (
              <div className="header-storage-bar">
                {pct !== null && pct > 0 && (
                  <div className="header-storage-bar-fill" style={{ width: `${pct}%` }} />
                )}
                {trashPct !== null && trashPct > 0 && (
                  <div className="header-storage-bar-fill" style={{ width: `${trashPct}%`, backgroundColor: 'var(--warning-color)' }} />
                )}
              </div>
            )}
          </div>
        </div>
        <ContextMenu container={userProfileRef} showOnClick align="right">
          {() => (
            <div>
              <div style={{ padding: '8px 14px 6px', borderBottom: '1px solid var(--border-color)', marginBottom: '4px' }}>
                <div style={{ fontSize: '13px', fontWeight: 600 }}>{user.email}</div>
                {hasStorage && storageInfo && (
                  <>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                      <span style={{ color: 'var(--accent-color)', fontWeight: 600 }}>{fileService.formatFileSize(storageInfo.usedSpace)}</span>
                      {' / '}
                      {storageInfo.storageLimit ? fileService.formatFileSize(storageInfo.storageLimit) : '∞'} used
                    </div>
                    {(storageInfo.trashUsedSpace || 0) > 0 && (
                      <div style={{ marginTop: '4px' }}>
                        <div style={{ fontSize: '11px', color: 'var(--warning-color)', marginTop: '2px' }}>
                          <span style={{ fontWeight: 600 }}>{fileService.formatFileSize(storageInfo.trashUsedSpace || 0)}</span> in trash
                        </div>
                        <div className="header-storage-bar" style={{ marginTop: '2px' }}>
                          <div className="header-storage-bar-fill" style={{ width: `${storageInfo.storageLimit > 0 ? Math.min(100, (storageInfo.trashUsedSpace || 0) / storageInfo.storageLimit * 100) : 0}%`, backgroundColor: 'var(--warning-color)' }} />
                        </div>
                      </div>
                    )}
                    {storageInfo.filesCount !== undefined && (
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '1px' }}>
                        {storageInfo.filesCount} file{storageInfo.filesCount !== 1 ? 's' : ''} total
                      </div>
                    )}
                  </>
                )}
              </div>
              <ContextMenuItem
                title={<><ImgSwap size={14} /> Share space</>}
                onClick={handleCreateShareSpace}
              />
              <ContextMenuItem
                title={<><ImgPower size={14} /> Sign out</>}
                onClick={handleLogout}
              />
            </div>
          )}
        </ContextMenu>
      </div>
    </header>
  );
}
